/**
 * SF-5084: whether a copy brings the source's images and report files (both off by default), how
 * far back, and how big those folders are on each side. The sizes are the daily storage totals,
 * so they can be up to a day old, and they are whole-folder sizes whatever the month limit.
 */
import { Checkbox, Form, Select, Space, Typography } from 'antd';
import { envLabel } from '../constants';
import { estimateLine, monthsLabel } from '../format';
import { useEnvCopyEstimate, type EnvCopyFileKind } from '../api/envCopyClient';

const { Text } = Typography;

/** The dropdown's value for no month limit (sent as filesMonths: null). */
const ALL_HISTORY = 'all';
const MONTH_OPTIONS = [
  ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: monthsLabel(i + 1) })),
  { value: ALL_HISTORY, label: 'All history' },
];

const KINDS: Record<EnvCopyFileKind, { label: string; missing: string }> = {
  images: { label: 'Copy images', missing: 'Pictures' },
  reports: { label: 'Copy report files', missing: 'Report files' },
};

interface FileCopyChoicesProps {
  source?: string;
  target?: string;
  copyImages: boolean;
  copyReports: boolean;
  /** null for all history. */
  filesMonths: number | null;
  onCopyImages: (value: boolean) => void;
  onCopyReports: (value: boolean) => void;
  onFilesMonths: (value: number | null) => void;
}

export const FileCopyChoices: React.FC<FileCopyChoicesProps> = ({
  source,
  target,
  copyImages,
  copyReports,
  filesMonths,
  onCopyImages,
  onCopyReports,
  onFilesMonths,
}) => {
  const chosen = !!source && !!target && source !== target;
  const query = useEnvCopyEstimate(source, target);
  const estimate = query.isError ? undefined : query.data?.data;
  const anyTicked = copyImages || copyReports;

  const sizeText = (kind: EnvCopyFileKind) => {
    if (!source || !target || source === target) return null;
    if (query.isInitialLoading) return 'Reading the folder sizes...';
    return estimateLine(kind, source, target, estimate);
  };

  const box = (kind: EnvCopyFileKind, checked: boolean, onChange: (v: boolean) => void) => {
    const size = sizeText(kind);
    const where = chosen
      ? `from ${envLabel(source)} will show as missing on ${envLabel(target)}`
      : 'from the source will show as missing on the target';
    // What a ticked box does (sync_files / sync_large_files in sf-api): copies each file that is
    // new or changed on the source and removes the target's files the source does not have.
    const from = chosen ? envLabel(source) : 'the source';
    const to = chosen ? envLabel(target) : 'the target';
    const which = filesMonths == null ? 'Files' : `Files from the last ${monthsLabel(filesMonths)}`;
    const does = `${which} that are new or changed on ${from} are copied, and files on ${to} that ${from} does not have are removed. The folder sizes cannot show how much that will be.`;
    return (
      <Space direction="vertical" size={0}>
        <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)}>
          {KINDS[kind].label}
        </Checkbox>
        {size && <Text type="secondary">{size}</Text>}
        {checked && <Text type="secondary">{does}</Text>}
        {!checked && <Text type="secondary">{`Not copied. ${KINDS[kind].missing} ${where}.`}</Text>}
      </Space>
    );
  };

  return (
    <Space direction="vertical">
      {box('images', copyImages, onCopyImages)}
      {box('reports', copyReports, onCopyReports)}
      {chosen && <Text type="secondary">Daily storage totals, can be up to a day old.</Text>}
      <Form.Item
        label="Only files from the last"
        htmlFor="env-copy-files-months"
        style={{ marginBottom: 0 }}
      >
        <Select
          id="env-copy-files-months"
          aria-label="Only files from the last"
          style={{ width: 160 }}
          disabled={!anyTicked}
          // 13 options: all rendered, none scrolled out of view.
          virtual={false}
          value={filesMonths ?? ALL_HISTORY}
          onChange={(value: number | string) =>
            onFilesMonths(typeof value === 'number' ? value : null)
          }
          options={MONTH_OPTIONS}
        />
      </Form.Item>
      {anyTicked && filesMonths != null && (
        <Text type="secondary">
          {`Whole folder sizes. Copying only the last ${monthsLabel(filesMonths)} copies less.`}
        </Text>
      )}
    </Space>
  );
};
